import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Settings, Globe, MessageSquare, FileText, RefreshCw, Save,
  Eye, Code, Mail, Phone, Grid, Trash2
} from 'lucide-react';
import ImageUpload from '../../components/ImageUpload';

const BACKEND_URL = '';

// Default categories configuration
const defaultCategories = [
  { category: 'google_ads', label: 'Google Ads', description: 'Maximize your Google Ads ROI', image_url: '', is_visible: true },
  { category: 'meta_ads', label: 'Meta Ads', description: 'Facebook & Instagram advertising', image_url: '', is_visible: true },
  { category: 'wordpress_support', label: 'WordPress Support', description: '24/7 expert WordPress assistance', image_url: '', is_visible: true },
  { category: 'wordpress_upgrades', label: 'WordPress Upgrades', description: 'Keep your site up to date', image_url: '', is_visible: true },
  { category: 'woocommerce_upgrades', label: 'WooCommerce', description: 'E-commerce enhancements', image_url: '', is_visible: true },
  { category: 'newsletter', label: 'Newsletter', description: 'Email marketing solutions', image_url: '', is_visible: true },
  { category: 'accessibility', label: 'Accessibility', description: 'WCAG compliance services', image_url: '', is_visible: true },
  { category: 'hosting', label: 'Hosting', description: 'Fast & secure WordPress hosting', image_url: '', is_visible: true },
  { category: 'email_client', label: 'Email Services', description: 'Professional email solutions', image_url: '', is_visible: true },
];

export default function SiteSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminAPI.getSiteSettings();
      setSettings(res.data);
    } catch (error) {
      toast.error('Failed to load settings');
      // Set defaults
      setSettings({
        site_name: 'Edrano',
        site_tagline: 'WordPress Hosting & Support',
        hero: {
          headline: 'Professional WordPress Solutions',
          subheadline: 'Hosting, support, and growth services for your WordPress business',
          cta_text: 'View Services',
          cta_link: '#services',
          background_image: ''
        },
        contact: {
          type: 'builtin',
          embed_code: '',
          email: '',
          phone: '',
          show_in_popup: true
        },
        footer_text: '© 2025 Edrano. All rights reserved.',
        show_prices_to_visitors: true,
        categories: defaultCategories
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSiteSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateHero = (field, value) => {
    setSettings({
      ...settings,
      hero: { ...settings.hero, [field]: value }
    });
  };

  const updateContact = (field, value) => {
    setSettings({
      ...settings,
      contact: { ...settings.contact, [field]: value }
    });
  };

  const updateCategory = (index, field, value) => {
    const newCategories = [...(settings.categories || defaultCategories)];
    newCategories[index] = { ...newCategories[index], [field]: value };
    setSettings({ ...settings, categories: newCategories });
  };

  const getCategoryImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${BACKEND_URL}${imageUrl}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="site-settings-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Settings className="h-6 w-6 text-primary" />
              </div>
              Site Settings
            </h1>
            <p className="text-muted-foreground mt-2">
              Customize your public landing page
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"
            >
              <Eye className="h-4 w-4" />
              Preview Site
            </a>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>

        <Tabs defaultValue="general">
          <TabsList className="grid w-full grid-cols-5 lg:w-[750px]">
          <TabsTrigger value="general" className="gap-2">
            <Settings className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="hero" className="gap-2">
            <Globe className="h-4 w-4" />
            Hero
          </TabsTrigger>
          <TabsTrigger value="categories" className="gap-2">
            <Grid className="h-4 w-4" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="contact" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            Contact
          </TabsTrigger>
          <TabsTrigger value="footer" className="gap-2">
            <FileText className="h-4 w-4" />
            Footer
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>Basic site information and branding</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload */}
              <div className="p-4 border rounded-lg bg-muted/30">
                <Label className="text-base font-semibold mb-3 block">Site Logo</Label>
                <div className="flex items-start gap-6">
                  {/* Logo Preview */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg overflow-hidden">
                      {settings.logo_url ? (
                        <img 
                          src={settings.logo_url.startsWith('http') ? settings.logo_url : `${BACKEND_URL}${settings.logo_url}`}
                          alt="Site Logo"
                          className="w-full h-full object-contain p-1"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <span className={`text-white font-bold text-xl ${settings.logo_url ? 'hidden' : ''}`}>
                        {(settings.site_name || 'UB').substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 text-center">Current</p>
                  </div>
                  
                  {/* Upload Controls */}
                  <div className="flex-1">
                    <ImageUpload
                      value={settings.logo_url || ''}
                      onChange={(url) => setSettings({ ...settings, logo_url: url })}
                      imageType="site"
                      label=""
                      compact={true}
                      showUrlInput={false}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Upload a square logo (recommended: 200x200px). Used in header navigation on public pages and admin panel.
                    </p>
                    {settings.logo_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSettings({ ...settings, logo_url: '' })}
                        className="text-destructive hover:text-destructive mt-2"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remove Logo
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="site_name">Site Name</Label>
                  <Input
                    id="site_name"
                    value={settings.site_name || ''}
                    onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                    placeholder="Edrano"
                  />
                </div>
                <div>
                  <Label htmlFor="site_tagline">Tagline</Label>
                  <Input
                    id="site_tagline"
                    value={settings.site_tagline || ''}
                    onChange={(e) => setSettings({ ...settings, site_tagline: e.target.value })}
                    placeholder="WordPress Hosting & Support"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <Label>Show Prices to Visitors</Label>
                  <p className="text-sm text-muted-foreground">
                    Display service prices on the public landing page
                  </p>
                </div>
                <Switch
                  checked={settings.show_prices_to_visitors}
                  onCheckedChange={(checked) => setSettings({ ...settings, show_prices_to_visitors: checked })}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hero Settings */}
        <TabsContent value="hero" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Hero Section</CardTitle>
              <CardDescription>The main banner at the top of your landing page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Badge Text */}
              <div>
                <Label htmlFor="badge_text">Badge Text (top of hero)</Label>
                <Input
                  id="badge_text"
                  value={settings.hero?.badge_text || ''}
                  onChange={(e) => updateHero('badge_text', e.target.value)}
                  placeholder="Trusted by 500+ businesses"
                />
              </div>

              <div>
                <Label htmlFor="headline">Headline</Label>
                <Input
                  id="headline"
                  value={settings.hero?.headline || ''}
                  onChange={(e) => updateHero('headline', e.target.value)}
                  placeholder="Professional WordPress Solutions"
                />
              </div>

              <div>
                <Label htmlFor="subheadline">Subheadline (Optional)</Label>
                <Textarea
                  id="subheadline"
                  value={settings.hero?.subheadline || ''}
                  onChange={(e) => updateHero('subheadline', e.target.value)}
                  placeholder="Hosting, support, and growth services..."
                  rows={2}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="cta_text">Primary Button Text</Label>
                  <Input
                    id="cta_text"
                    value={settings.hero?.cta_text || ''}
                    onChange={(e) => updateHero('cta_text', e.target.value)}
                    placeholder="Get Started"
                  />
                </div>
                <div>
                  <Label htmlFor="cta_link">Primary Button Link</Label>
                  <Input
                    id="cta_link"
                    value={settings.hero?.cta_link || ''}
                    onChange={(e) => updateHero('cta_link', e.target.value)}
                    placeholder="#services or /contact or #contact"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="secondary_btn_text">Secondary Button Text</Label>
                  <Input
                    id="secondary_btn_text"
                    value={settings.hero?.secondary_btn_text || ''}
                    onChange={(e) => updateHero('secondary_btn_text', e.target.value)}
                    placeholder="Client Portal"
                  />
                </div>
                <div>
                  <Label htmlFor="secondary_btn_link">Secondary Button Link</Label>
                  <Input
                    id="secondary_btn_link"
                    value={settings.hero?.secondary_btn_link || ''}
                    onChange={(e) => updateHero('secondary_btn_link', e.target.value)}
                    placeholder="/login"
                  />
                </div>
              </div>

              <ImageUpload
                value={settings.hero?.background_image || ''}
                onChange={(url) => updateHero('background_image', url)}
                imageType="site"
                label="Background Image (Optional - uses gradient if empty)"
                previewClassName="h-32 w-full object-cover"
              />
            </CardContent>
          </Card>

          {/* Categories Section Settings */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Categories Section</CardTitle>
              <CardDescription>The service categories grid below the hero</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="categories_badge">Badge Text</Label>
                <Input
                  id="categories_badge"
                  value={settings.categories_section?.badge || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    categories_section: { ...settings.categories_section, badge: e.target.value }
                  })}
                  placeholder="OUR SERVICES"
                />
              </div>
              <div>
                <Label htmlFor="categories_title">Title</Label>
                <Input
                  id="categories_title"
                  value={settings.categories_section?.title || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    categories_section: { ...settings.categories_section, title: e.target.value }
                  })}
                  placeholder="Solutions built for growth"
                />
              </div>
              <div>
                <Label htmlFor="categories_description">Description</Label>
                <Textarea
                  id="categories_description"
                  value={settings.categories_section?.description || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    categories_section: { ...settings.categories_section, description: e.target.value }
                  })}
                  placeholder="Choose a category to explore..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* CTA Section Settings */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>CTA Section</CardTitle>
              <CardDescription>The call-to-action section with gradient background</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="cta_section_title">Title</Label>
                <Input
                  id="cta_section_title"
                  value={settings.cta_section?.title || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    cta_section: { ...settings.cta_section, title: e.target.value }
                  })}
                  placeholder="Ready to accelerate your growth?"
                />
              </div>
              <div>
                <Label htmlFor="cta_section_description">Description</Label>
                <Textarea
                  id="cta_section_description"
                  value={settings.cta_section?.description || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    cta_section: { ...settings.cta_section, description: e.target.value }
                  })}
                  placeholder="Join hundreds of businesses..."
                  rows={2}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="cta_primary_btn">Primary Button Text</Label>
                  <Input
                    id="cta_primary_btn"
                    value={settings.cta_section?.primary_btn || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      cta_section: { ...settings.cta_section, primary_btn: e.target.value }
                    })}
                    placeholder="Contact Us"
                  />
                </div>
                <div>
                  <Label htmlFor="cta_secondary_btn">Secondary Button Text</Label>
                  <Input
                    id="cta_secondary_btn"
                    value={settings.cta_section?.secondary_btn || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      cta_section: { ...settings.cta_section, secondary_btn: e.target.value }
                    })}
                    placeholder="Create Free Account"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Settings */}
        <TabsContent value="categories" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Category Images</CardTitle>
              <CardDescription>Upload custom images for each service category displayed on the landing page</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                {(settings.categories || defaultCategories).map((cat, index) => (
                  <div 
                    key={cat.category} 
                    className="flex items-start gap-4 p-4 bg-muted/50 rounded-xl border"
                  >
                    {/* Category Image Preview */}
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500/10 to-violet-500/10 flex items-center justify-center overflow-hidden border-2 border-white shadow-sm">
                        {cat.image_url ? (
                          <img 
                            src={getCategoryImageUrl(cat.image_url)} 
                            alt={cat.label}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">No image</span>
                        )}
                      </div>
                    </div>

                    {/* Category Details */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{cat.label}</h4>
                          <p className="text-sm text-muted-foreground">{cat.category}</p>
                        </div>
                        <Switch
                          checked={cat.is_visible !== false}
                          onCheckedChange={(checked) => updateCategory(index, 'is_visible', checked)}
                        />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Display Name</Label>
                          <Input
                            value={cat.label}
                            onChange={(e) => updateCategory(index, 'label', e.target.value)}
                            placeholder="Category name"
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Description</Label>
                          <Input
                            value={cat.description || ''}
                            onChange={(e) => updateCategory(index, 'description', e.target.value)}
                            placeholder="Short description"
                            className="h-9"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs">Category Image (Round Icon)</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <ImageUpload
                            value={cat.image_url || ''}
                            onChange={(url) => updateCategory(index, 'image_url', url)}
                            imageType="category"
                            label=""
                            compact={true}
                            previewClassName="hidden"
                          />
                          {cat.image_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateCategory(index, 'image_url', '')}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Upload a square image (recommended: 200x200px). Will be displayed in a round mask.
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Settings */}
        <TabsContent value="contact" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Form</CardTitle>
              <CardDescription>Configure the contact popup/form on your landing page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Contact Form Type</Label>
                <Select
                  value={settings.contact?.type || 'builtin'}
                  onValueChange={(value) => updateContact('type', value)}
                >
                  <SelectTrigger className="w-full mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="builtin">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Built-in Contact Form
                      </div>
                    </SelectItem>
                    <SelectItem value="embed">
                      <div className="flex items-center gap-2">
                        <Code className="h-4 w-4" />
                        Embed External Form (iframe/script)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {settings.contact?.type === 'embed' ? (
                <div className="space-y-4 p-4 bg-muted rounded-lg">
                  <div>
                    <Label htmlFor="embed_code">Embed Code</Label>
                    <Textarea
                      id="embed_code"
                      value={settings.contact?.embed_code || ''}
                      onChange={(e) => updateContact('embed_code', e.target.value)}
                      placeholder="<iframe src='...'></iframe> or <script>...</script>"
                      rows={6}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Paste your form embed code from Encharge.io, HubSpot, Typeform, etc.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    The built-in form sends submissions to your admin email and stores them in the database.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact_email" className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Notification Email
                      </Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={settings.contact?.email || ''}
                        onChange={(e) => updateContact('email', e.target.value)}
                        placeholder="admin@example.com"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Receive form submissions at this email
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="contact_phone" className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        Phone (Optional)
                      </Label>
                      <Input
                        id="contact_phone"
                        value={settings.contact?.phone || ''}
                        onChange={(e) => updateContact('phone', e.target.value)}
                        placeholder="+30 123 456 7890"
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Footer Settings */}
        <TabsContent value="footer" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Footer</CardTitle>
              <CardDescription>Customize the footer of your landing page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="footer_text">Footer Text</Label>
                <Input
                  id="footer_text"
                  value={settings.footer_text || ''}
                  onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                  placeholder="© 2025 Company Name. All rights reserved."
                />
              </div>

              {/* Preview */}
              <div className="p-4 bg-slate-900 rounded-lg text-center text-gray-400 text-sm">
                {settings.footer_text || '© 2025 Your Company'}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </Layout>
  );
}
